import axios from 'axios';
import { unwrapErrorDetail } from './errors';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── Module guard ───────────────────────────────────────────────────────────────
// URL prefix → module technical_name (matches backend AppAccessMiddleware)
const MODULE_URL_MAP = {
  'helpdesk/':            'helpdesk',
  'partner/':            'partner',
  'inbox/':              'inbox',
  'marketing/':          'marketing',
  'knowledge-system/':   'knowledge_system',
  'csat/':               'customer_satisfaction',
  'nexus/':              'nexus',
  'projects/':           'projects',
  'huddle/':             'huddle',
  'sequences/':         'sequences',
  'compliance/':         'compliance',
  'regulatory/':         'regulatory',
  'voice-desk/':         'voice_desk',
  'voice/':              'voice',
  'admin-monitoring/':   'admin_monitoring',
};

// Global store for installed apps (synced from InstalledAppsProvider)
let _installedApps = new Set();
let _appsLoaded = false;

export const setInstalledApps = (apps) => {
  _installedApps = apps instanceof Set ? apps : new Set(apps);
  _appsLoaded = true;
};

// Paths that must succeed even when their owning module is not installed
// (mirrors backend AppAccessMiddleware.MODULE_PATH_BYPASS). Heartbeat +
// activity tracking power tenant-wide presence and must never be gated.
const MODULE_PATH_BYPASS = [
  'admin-monitoring/presence/heartbeat/',
  'admin-monitoring/presence/manual-status/',
  'admin-monitoring/presence/me/',
  'admin-monitoring/presence/stream/',
  'admin-monitoring/activity/track-access/',
];

// Request interceptor to block API calls to uninstalled modules
api.interceptors.request.use(
  (config) => {
    const url = config.url || '';
    if (process.env.NODE_ENV === 'development') {
      console.log('[api] REQUEST', config.method?.toUpperCase(), url);
    }

    // Only block if apps have been loaded at least once
    // This prevents blocking requests during initial load
    if (!_appsLoaded) {
      return config;
    }

    // Always allow bypass paths regardless of install state
    if (MODULE_PATH_BYPASS.some((p) => url.startsWith(p))) {
      return config;
    }

    // Check if this is a module-specific API call
    for (const [prefix, moduleName] of Object.entries(MODULE_URL_MAP)) {
      if (url.startsWith(prefix)) {
        // Skip if not installed
        if (!_installedApps.has(moduleName)) {
          return Promise.reject({
            isModuleNotInstalled: true,
            module: moduleName,
            message: `Module '${moduleName}' is not installed`,
          });
        }
        break;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ── Token storage helpers ─────────────────────────────────────────────────────
// JWT tokens live in HttpOnly cookies — never in localStorage.
// clearAllTokens wipes every auth-related localStorage key so that after a
// full-page redirect the AuthProvider re-initializes as unauthenticated.
export const clearAllTokens = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('tenant_schema');
  localStorage.removeItem('userdetails');
  localStorage.removeItem('timezone');
  localStorage.removeItem('company_timezone');
  localStorage.removeItem('company_currency');
};

// ── Silent refresh helper ─────────────────────────────────────────────────────
// Sends an empty POST to /auth/jwt/refresh/. The refresh token travels as an
// HttpOnly cookie (withCredentials: true); the backend sets new access +
// refresh cookies in the response. No token ever touches JavaScript.
// Multiple concurrent 401s share the same promise to avoid redundant refreshes.
let _refreshPromise = null;

// Exported for EventSource callers. SSE is a browser primitive that never goes
// through axios, so an SSE 401 cannot reach the response interceptor below —
// the stream would otherwise retry forever against a cookie the browser has
// already dropped (JWT_AUTH_COOKIE_MAX_AGE matches the 15-minute access token,
// so it is deleted, not merely stale). Callers await this before reconnecting.
// Sharing `_refreshPromise` means a stream reconnect and a concurrent axios 401
// still make one refresh request between them.
export const silentRefresh = async () => {
  if (_refreshPromise) return _refreshPromise;

  _refreshPromise = (async () => {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL || '/api'}/auth/jwt/refresh/`,
      {},
      { withCredentials: true },
    );
    // The refresh carries the caller's current permission fields, so a group
    // change lands on this 15-minute cycle at no extra request. We are inside
    // an interceptor with no React context, so hand it to AuthProvider the same
    // way an app install already reaches InstalledAppsProvider. Optional on
    // purpose: an older backend simply omits it and this is a no-op.
    const userdetails = res?.data?.userdetails;
    if (userdetails) {
      window.dispatchEvent(
        new CustomEvent('auth-userdetails', { detail: { userdetails } }),
      );
    }
  })();

  try {
    return await _refreshPromise;
  } finally {
    _refreshPromise = null;
  }
};

// ── Request interceptor ───────────────────────────────────────────────────────
// JWT access token is sent automatically as an HttpOnly cookie (withCredentials).
// No Authorization header needed — cookie auth wins on the backend.
api.interceptors.request.use(
  (config) => {
    const language = localStorage.getItem('language') || 'en';
    config.headers['Accept-Language'] = language;

    // Superusers hitting SaaS admin routes should NOT send X-Tenant-Id so the
    // middleware can keep connection on public schema (cross-tenant bypass).
    const tenantSchema = localStorage.getItem('tenant_schema');
    const url = config.url || '';
    const userdetails = (() => {
      try { return JSON.parse(localStorage.getItem('userdetails') || 'null'); } catch { return null; }
    })();
    // Backup & Export lives under /platform/ and is cross-tenant for the same
    // reason SaaS admin is: it reads Client rows from the public schema.
    const isSaaSAdminRequest =
      url.includes('/apps/saas-admin/') || url.includes('/platform/');
    const isSuperuser = userdetails && userdetails.is_superuser;

    if (tenantSchema && !(isSaaSAdminRequest && isSuperuser)) {
      config.headers['X-Tenant-Id'] = tenantSchema;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ── Endpoints that change app installation status ────────────────────────────
const APP_INSTALL_ENDPOINTS = [
  '/apps/store/',
  '/apps/saas-admin/',
];

// ── Response interceptor ──────────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[api] SUCCESS', response.config.method, response.config.url, response.status);
    }
    // After successful app installation/uninstallation calls, refresh the
    // installed apps cache to ensure immediate permission update without
    // requiring manual refresh or cache clear.
    // Only a state change (POST install/uninstall, SaaS-admin toggles) should
    // trigger a refresh. Matching every GET under /apps/store/ made each App
    // Store listing fetch refetch the installed-apps cache, which re-rendered
    // the store, which fetched the listing again.
    const url = response.config?.url || '';
    const method = (response.config?.method || 'get').toLowerCase();
    const isAppEndpoint = method !== 'get' && APP_INSTALL_ENDPOINTS.some(endpoint => url.includes(endpoint));
    if (isAppEndpoint) {
      window.dispatchEvent(new CustomEvent('company-updated', { 
        detail: { source: 'api-response', url } 
      }));
    }
    return response;
  },
  async (error) => {
    unwrapErrorDetail(error.response?.data);
    const status         = error.response?.status;
    const originalConfig = error.config;

    // On 401: attempt silent cookie-based refresh then retry the original request.
    // The refresh endpoint reads the HttpOnly refresh cookie — no token in JS.
    // If refresh also fails (expired/missing cookie), clear local state and redirect.
    if (status === 401 && !originalConfig._retried) {
      originalConfig._retried = true; // prevent infinite retry loops

      try {
        await silentRefresh();
        return api(originalConfig); // cookies updated — retry with fresh access cookie
      } catch {
        // Refresh token expired or missing — full session teardown.
        // Clear localStorage so AuthProvider re-initializes as unauthenticated on reload.
        clearAllTokens();
        const path = window.location.pathname;
        const publicExactPaths = ['/login', '/register', '/'];
        const publicPrefixes = [
          '/verify-email',
          '/s/',
          '/proposals/preview/',
          '/lead-form',
          '/sign/',
          '/schedule/',
          '/portal/',
        ];
        const isPublic = publicExactPaths.includes(path) || publicPrefixes.some(prefix => path.startsWith(prefix));
        if (!isPublic) {
          window.location.href = '/login?reason=session_expired';
        }
        return Promise.reject(error);
      }
    }

    // ── Module not installed (403 from AppAccessMiddleware) ────────────────
    // Don't redirect to /unauthorized - that breaks access to other pages like dashboard.
    // Instead, just reject the promise so components can handle it gracefully.
    // The ProtectedRoute should already block navigation to uninstalled module routes.
    if (status === 403 && error.response?.data?.code === 'module_not_installed') {
      return Promise.reject(error);
    }

    // ── AI features disabled (403 from the premium AI gate) ────────────────
    // Stale-UI safety net: if the SaaS admin turned AI off mid-session, an
    // in-page AI call lands here. Refresh the feature flag so all AI UI
    // hides, then reject so the caller can render its own fallback.
    if (status === 403 && (error.response?.data?.code === 'ai_disabled'
        || error.response?.data?.detail?.code === 'ai_disabled')) {
      window.dispatchEvent(new CustomEvent('company-updated', {
        detail: { source: 'ai-disabled', url: error.config?.url }
      }));
      return Promise.reject(error);
    }

    // ── Module trial expired (403 from AppAccessMiddleware) ────────────────
    // Catches in-page API calls after the trial lapses mid-session: refresh the
    // installed-apps cache (so Sidebar/ProtectedRoute pick up the expiry) and
    // land the user on the trial-expired screen.
    if (status === 403 && error.response?.data?.code === 'module_trial_expired') {
      window.dispatchEvent(new CustomEvent('company-updated', {
        detail: { source: 'trial-expired', url: error.config?.url }
      }));
      if (window.location.pathname !== '/trial-expired') {
        window.location.href = '/trial-expired';
      }
      return Promise.reject(error);
    }

    // Only log in development — never expose internals in production
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('API Error:', {
        status,
        url:  error.config?.url,
        data: error.response?.data,
      });
    }

    return Promise.reject(error);
  },
);

// no-op — kept so the ~270 call sites in components compile without changes.
// JWT cookie handles auth; no Authorization header needed.
export const setAuthToken = () => {};

// ── Request config builder ────────────────────────────────────────────────────
export const createRequestConfig = (customHeaders = {}, customBody = {}) => ({
  headers: {
    ...api.defaults.headers,
    ...customHeaders,
  },
  data: customBody,
});

export default api;

// ── MFA (TOTP) helpers ────────────────────────────────────────────────────────
// Authenticated management calls use the `api` instance (token auto-attached).
// login-verify is pre-auth, so it uses a bare axios call like Login.jsx.
export const getMfaStatus  = ()         => api.get('/auth/mfa/status/');
export const mfaSetup      = ()         => api.post('/auth/mfa/setup/', {});
export const mfaConfirm    = (code)     => api.post('/auth/mfa/confirm/', { code });
export const mfaDisable    = (password) => api.post('/auth/mfa/disable/', { password });
export const mfaRegenerate = (password) => api.post('/auth/mfa/recovery/regenerate/', { password });

export const mfaLoginVerify = ({ mfa_token, code }) =>
  axios.post(
    `${process.env.REACT_APP_API_URL || '/api'}/auth/mfa/login-verify/`,
    { mfa_token, code },
    { headers: { 'Content-Type': 'application/json' }, withCredentials: true },
  );

// ── WhatsApp OTP method ───────────────────────────────────────────────────────
export const whatsappStatus       = ()         => api.get('/auth/mfa/whatsapp/status/');
export const whatsappSendCode     = ()         => api.post('/auth/mfa/whatsapp/send-code/', {});
export const whatsappConfirm      = (code)     => api.post('/auth/mfa/whatsapp/confirm/', { code });
export const updateMyPhone        = (phone)    => api.patch('/auth/users/me/', { phone });
export const getWhatsappTemplateConfig = ()    => api.get('/auth/mfa/whatsapp/template-config/');
export const setWhatsappTemplateConfig = (otp_template_name, otp_template_language) =>
  api.put('/auth/mfa/whatsapp/template-config/', { otp_template_name, otp_template_language });

// Pre-auth (login) resend — bare axios like mfaLoginVerify.
export const whatsappLoginResend = (mfa_token) =>
  axios.post(
    `${process.env.REACT_APP_API_URL || '/api'}/auth/mfa/whatsapp/login-resend/`,
    { mfa_token },
    { headers: { 'Content-Type': 'application/json' }, withCredentials: true },
  );

// ── Server-side logout helper ─────────────────────────────────────────────────
// Calls /api/auth/logout/ to invalidate the server-side Token + JWT refresh,
// then clears all local auth state regardless of server response.
export const logout = async () => {
  try {
    // Empty body — server reads the refresh token from the HttpOnly cookie
    await api.post('/auth/logout/', {});
  } catch {
    // Server-side invalidation failed (e.g. token already expired) — still clear locally
  } finally {
    clearAllTokens();
  }
};

// ── Tenant settings, currencies and exchange rates ────────────────────────────
export const getTenantSettings    = ()                    => api.get('/common/tenant-settings/');
export const updateTenantSettings = (body)                => api.patch('/common/tenant-settings/', body);
export const getCurrencies        = ()                    => api.get('/common/currencies/');
export const getExchangeRates     = ()                    => api.get('/common/exchange-rates/');
export const putExchangeRate      = (code, units_per_base) => api.put(`/common/exchange-rates/${code}/`, { units_per_base });
export const deleteExchangeRate   = (code)                => api.delete(`/common/exchange-rates/${code}/`);
